import { type CsvCell, toCsv } from "@plerity/csv";
import { Download } from "lucide-react";
import { useCallback } from "react";

// Data export accepts any serializable object shape. `object` (not
// `Record<string, unknown>`) is deliberate: nominal row interfaces without an
// explicit index signature (e.g. BarDistributionRow, TimeSeriesChart's
// `{ date, value }`) aren't structurally assignable to `Record<string, unknown>`
// even though every property matches — `object` has no such restriction.
type ExportableData = object[] | object | null;

export interface DataExportProps {
  data: ExportableData;
  filenamePrefix: string;
  format?: "csv" | "json";
  label?: string;
  size?: "sm" | "md";
}

/** Nested values are serialised as JSON instead of the useless "[object Object]". */
function toCell(value: unknown): CsvCell {
  if (value == null) return null;
  if (typeof value === "object" && !(value instanceof Date)) return JSON.stringify(value);
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value instanceof Date) {
    return value;
  }
  return String(value);
}

/** One implementation of RFC-4180 escaping + formula-injection neutralisation: `@plerity/csv`. Exported for tests. */
export function rowsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  return toCsv(
    headers,
    rows.map((row) => headers.map((h) => toCell(row[h])))
  );
}

function toJson(rows: Record<string, unknown>[]): string {
  return JSON.stringify(rows, null, 2);
}

function triggerDownload(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function DataExport({ data, filenamePrefix, format = "csv", label, size = "sm" }: DataExportProps) {
  const handleExport = useCallback(() => {
    if (!data) return;

    // Narrow, local cast: by this point `rows` is just a plain array of
    // objects (whatever nominal shape the caller passed) being iterated
    // generically by key — rowsToCsv/toJson never assume specific keys exist.
    const rows = (Array.isArray(data) ? data : [data]) as Record<string, unknown>[];
    if (rows.length === 0) return;

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[-:T]/g, (m) => (m === "T" ? "_" : ""));

    if (format === "csv") {
      triggerDownload(rowsToCsv(rows), `${filenamePrefix}_${timestamp}.csv`, "text/csv");
    } else {
      triggerDownload(toJson(rows), `${filenamePrefix}_${timestamp}.json`, "application/json");
    }
  }, [data, filenamePrefix, format]);

  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  const isSm = size === "sm";

  return (
    <button
      type="button"
      onClick={handleExport}
      className={`inline-flex items-center gap-1.5 rounded-md border border-default bg-surface font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:text-gray-300 dark:hover:bg-gray-700 ${
        isSm ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
      }`}
    >
      <Download className={isSm ? "h-3 w-3" : "h-4 w-4"} aria-hidden="true" />
      {label ?? `Export ${format.toUpperCase()}`}
    </button>
  );
}

export interface DataExportSectionProps {
  items: { name: string; data: object[] | null }[];
  title?: string;
}

export function DataExportSection({ items, title = "Export Data" }: DataExportSectionProps) {
  const validItems = items.filter((i) => i.data && i.data.length > 0);
  if (validItems.length === 0) return null;

  return (
    <div className="rounded-lg border border-light bg-surface p-4">
      <h4 className="mb-3 text-sm font-medium text-fg">{title}</h4>
      <div className="space-y-2">
        {validItems.map((item) => (
          <div key={item.name} className="flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
              {item.data && (
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-400">{item.data.length} records</span>
              )}
            </div>
            <DataExport data={item.data} filenamePrefix={item.name.toLowerCase().replace(/\s+/g, "_")} />
          </div>
        ))}
      </div>
    </div>
  );
}
